import { ref, computed, watch } from 'vue';

/**
 * AuditReadiness (v3 homepage) — live data for the V3Audit widget, from
 * `POST Report/auditReadiness`: a ready% + gap rows (missing rationale /
 * incomplete scoring / unresolved anomalies) with real counts. `self` pattern;
 * caller injects an axios-like `http`. When disabled (the widget is in static
 * mode) it doesn't fetch and the presentation falls back to authored values.
 *
 * @param {Object} options
 * @param {{post:Function}} options.http
 * @param {() => boolean} [options.getEnabled] fetch live (default true)
 * @param {() => void} [options.onReady] called once after the first load settles
 */
export function auditReadiness(options) {
  const { http, getEnabled = () => true, onReady = () => {} } = options || {};

  const self = { ready: ref(0), gaps: ref([]), total: ref(0), loading: ref(false) };

  self.isLoading = computed(() => self.loading.value);
  self.openCount = computed(() => self.gaps.value.reduce((s, g) => s + (Number(g.count) || 0), 0));

  let didReady = false;
  function signalReady() {
    if (!didReady) {
      didReady = true;
      onReady();
    }
  }

  async function load() {
    if (!getEnabled()) {
      signalReady();
      return;
    }
    self.loading.value = true;
    try {
      const resp = await http.post('Report/auditReadiness', {});
      const data = (resp && resp.data && (resp.data.Data ?? resp.data)) || {};
      self.ready.value = Number(data.ready) || 0;
      self.gaps.value = Array.isArray(data.gaps) ? data.gaps : [];
      self.total.value = Number(data.total) || 0;
    } catch {
      self.ready.value = 0;
      self.gaps.value = [];
    } finally {
      self.loading.value = false;
      signalReady();
    }
  }

  watch(() => getEnabled(), load, { immediate: true });

  return self;
}
